import { useState, type ReactNode } from "react";
import { useI18n } from "@/lib/i18n";
import { z } from "zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const BUSINESS_TYPES = [
  "Content Creator",
  "Credit Repair",
  "Real Estate",
  "Medical Spa",
  "Lawyer",
  "Funding/Finance",
  "Coach/Consultant",
  "Contractor/Broker",
  "Other",
];

const schema = z.object({
  full_name: z.string().trim().min(1, "Name is required").max(100),
  email: z.string().trim().email("Invalid email").max(255),
  business_type: z.string().min(1, "Select a business type"),
});

export function WaitlistDialog({ children, plan }: { children: ReactNode; plan?: { name: string; price: number } }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", business_type: "" });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setLoading(true);
    const { error } = await supabase.from("waitlist").insert(parsed.data);
    if (error) {
      setLoading(false);
      toast.error(t("common.somethingWrong"));
      return;
    }
    supabase.functions
      .invoke("send-waitlist-confirmation", {
        body: { full_name: parsed.data.full_name, email: parsed.data.email, variant: "main" },
      })
      .catch((err) => console.error("waitlist email failed", err));
    setLoading(false);
    setSubmitted(true);
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setTimeout(() => {
        setSubmitted(false);
        setForm({ full_name: "", email: "", business_type: "" });
      }, 200);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        {submitted ? (
          <div className="text-center py-6">
            <div className="w-14 h-14 rounded-full bg-primary/15 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-primary" />
            </div>
            <DialogTitle className="text-2xl mb-2">You're on the list!</DialogTitle>
            <DialogDescription className="text-base">
              We'll reach out when AdKavi goes live.
            </DialogDescription>
            <Button onClick={() => handleOpenChange(false)} className="mt-6 w-full">Close</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl">Join the Waitlist</DialogTitle>
              <DialogDescription>
                {plan
                  ? `Be the first to know when AdKavi goes live. First 100 total founding members can lock in ${plan.name} at $${plan.price}/mo.`
                  : "Be the first to know when AdKavi goes live. First 100 total founding members can choose any plan."}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={onSubmit} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name</Label>
                <Input id="full_name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} maxLength={100} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} maxLength={255} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="business_type">Business Type</Label>
                <Select value={form.business_type} onValueChange={(v) => setForm({ ...form, business_type: v })}>
                  <SelectTrigger id="business_type">
                    <SelectValue placeholder="Select your business type" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUSINESS_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-orange-500 hover:bg-orange-600 text-white">
                {loading ? "Joining…" : "Join Waitlist — Lock In This Price"}
              </Button>
              <p className="text-xs text-muted-foreground text-center pt-1">
                Your founding member price stays active as long as your subscription remains active. If you cancel and rejoin later, regular pricing applies.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
